import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useHistory } from 'react-router';
import { debounce } from 'lodash';
import { useTrack } from '@hackler/react-sdk';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { requestEnumToString } from '@utils/enumToString';
import { CapaPcbServiceType } from '@generated/graphql';
import * as assets from '@assets';
import useQueryString from '@hooks/useQueryString';
import {
  PcbRequestStageEnum,
  RequestQueryString
} from '@routes/NewRequest/types';
import RequestPreview from '@routes/NewRequest/RequestPreview';
import RequestBottomNav from '@routes/NewRequest/Layout/RequestBottomNav';
import RequestLayout from '../../Layout/RequestCommonLayout';
import PannelLayout from '../../Layout/PannelLayout';
import PCBRequestForm from '../PCB/PCBRequestForm';
import PCBProcessSelect from './PCBProcessSelect';

const steps: PcbRequestStageEnum[] = [
  PcbRequestStageEnum.PROCESS_SELECT,
  PcbRequestStageEnum.PCB,
  PcbRequestStageEnum.PREVIEW,
  PcbRequestStageEnum.COMPLETE
];

const PCBRequest: React.FC = () => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const [buttonStateHandler, setButtonStateHandler] = useState<{
    disableHandler: (value: boolean) => void;
  }>({
    disableHandler: (value) => undefined
  });
  const nextButtonAction = useRef<
    Partial<Record<PcbRequestStageEnum, () => void>>
  >({
    [PcbRequestStageEnum.PROCESS_SELECT]: () => undefined,
    [PcbRequestStageEnum.PCB]: () => undefined,
    [PcbRequestStageEnum.PREVIEW]: () => undefined
  });
  const currentEventState = useRef<{
    selectedProcess: string;
    exitStage: PcbRequestStageEnum;
  }>({
    selectedProcess: '',
    exitStage: '' as PcbRequestStageEnum
  });
  const history = useHistory<{ fromLandingPage?: boolean }>();
  const track = useTrack();
  const {
    query: { process, stage },
    onAddQueryString
  } = useQueryString<RequestQueryString & { stage: PcbRequestStageEnum }>();
  const processArray = process?.split(',');
  const currentStageIndex = steps.findIndex((step) => step === stage);
  const currentStage = useRef<PcbRequestStageEnum | null>(null);
  const lastStage = useRef<PcbRequestStageEnum | null>(null);
  const currentProcess = useRef<string | null>(null);
  const buttonAction = nextButtonAction.current?.[stage as PcbRequestStageEnum];

  const titleRender = useMemo(() => {
    switch (stage) {
      case PcbRequestStageEnum.PROCESS_SELECT:
        return `필요한 작업을
        모두 선택해주세요.`;
      case PcbRequestStageEnum.PCB:
        return '요청서를 작성해주세요.';
      case PcbRequestStageEnum.PREVIEW:
        return `마지막으로 요청서를
        확인해주세요.`;
    }
  }, [stage]);

  const contentRender = useMemo(() => {
    switch (stage) {
      case PcbRequestStageEnum.PROCESS_SELECT:
        return (
          <PCBProcessSelect
            setDisabledNextButton={buttonStateHandler.disableHandler}
            nextButtonAction={nextButtonAction}
          />
        );
      case PcbRequestStageEnum.PCB:
        return (
          <PCBRequestForm
            setDisabledNextButton={buttonStateHandler.disableHandler}
            nextButtonAction={nextButtonAction}
          />
        );
      case PcbRequestStageEnum.PREVIEW:
        return (
          <RequestPreview
            setDisabledNextButton={buttonStateHandler.disableHandler}
            nextButtonAction={nextButtonAction}
          />
        );
    }
  }, [stage, buttonStateHandler.disableHandler]);

  const extraDescription = useMemo(() => {
    if (!processArray) return;
    const pcbOrder: string[] = [
      CapaPcbServiceType.CircuitDesign,
      CapaPcbServiceType.CircuitFabrication,
      CapaPcbServiceType.CircuitAssembly
    ];

    return pcbOrder
      .reduce((acc, service) => {
        if (processArray.includes(service)) {
          acc.push(requestEnumToString(service as CapaPcbServiceType));
        }
        return acc;
      }, [] as string[])
      .join(', ');
  }, [processArray]);

  const onMoveNextStage = useCallback(() => {
    if (
      stage !== PcbRequestStageEnum.COMPLETE &&
      stage !== PcbRequestStageEnum.PREVIEW
    ) {
      onAddQueryString({
        stage: steps[currentStageIndex + 1]
      });
    }

    if (buttonAction) {
      buttonAction();
    }
  }, [onAddQueryString, currentStageIndex, stage, buttonAction]);

  const onMovePrevStage = useCallback(() => {
    if (stage !== PcbRequestStageEnum.PREVIEW) {
      if (buttonAction) {
        buttonAction();
      }
    }

    if (currentStageIndex === 0) {
      history.push('/request');
    } else {
      onAddQueryString({
        stage: steps[currentStageIndex - 1]
      });
    }
  }, [currentStageIndex, onAddQueryString, history, stage, buttonAction]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (stage) {
      currentStage.current = stage;
    }
  }, [stage]);

  useEffect(() => {
    if (process) {
      currentProcess.current = process;
    }
  }, [process]);

  useEffect(() => {
    const nextButton = nextButtonAction.current?.[stage as PcbRequestStageEnum];
    return () => {
      if (stage !== PcbRequestStageEnum.PREVIEW) {
        if (nextButton) {
          nextButton();
        }
      }
    };
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    currentEventState.current = {
      exitStage: !lastStage.current
        ? ('' as PcbRequestStageEnum)
        : lastStage.current,
      selectedProcess: (process ?? '').replace(/CIRCUIT_/g, '')
    };
  }, [process]);

  useEffect(() => {
    if (stage) {
      if (!lastStage.current) {
        lastStage.current = stage;
      }

      if (
        steps.findIndex((step) => step === stage) >
        steps.findIndex((step) => step === lastStage.current)
      ) {
        lastStage.current = stage;
      }
    }
  }, [stage]);

  useEffect(() => {
    const current = currentStage.current;

    return () => {
      if (
        current &&
        current !== PcbRequestStageEnum.COMPLETE &&
        lastStage.current !== PcbRequestStageEnum.COMPLETE
      ) {
        track({
          key: 'exit',
          properties: {
            exitStage: lastStage.current ?? '',
            selectedProcess: (currentProcess.current ?? '').replace(
              /CIRCUIT_/g,
              ''
            )
          }
        });
      }
    };
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    const current = currentStage.current;

    const sendTrackingData = () => {
      if (
        current &&
        current !== PcbRequestStageEnum.COMPLETE &&
        lastStage.current !== PcbRequestStageEnum.COMPLETE
      ) {
        track({
          key: 'exit',
          properties: {
            exitStage: lastStage.current ?? '',
            selectedProcess: (currentProcess.current ?? '').replace(
              /CIRCUIT_/g,
              ''
            )
          }
        });
      }
    };

    window.addEventListener('beforeunload', sendTrackingData);

    return () => {
      window.removeEventListener('beforeunload', sendTrackingData);
    };
  });

  return (
    <RequestLayout
      matches={matches}
      title={titleRender}
      titleBackgroundImage={assets.ServiceType_Pcb}
      serviceExtraDescription={
        stage !== PcbRequestStageEnum.PROCESS_SELECT
          ? extraDescription
          : undefined
      }
      onClickClose={() => history.push('/request')}
    >
      <PannelLayout
        content={contentRender}
        bottomNav={
          stage && (
            <RequestBottomNav
              steps={steps}
              stage={stage}
              setButtonStateHandler={setButtonStateHandler}
              onClickNext={debounce(onMoveNextStage, 50)}
              onClickBack={debounce(onMovePrevStage, 50)}
            />
          )
        }
      />
    </RequestLayout>
  );
};

export default PCBRequest;
